import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  UserPlus,
  Users,
  FileText,
  ShoppingCart,
  Calendar,
  GraduationCap,
  BookOpen,
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const quickActions = [
  {
    icon: UserPlus,
    title: 'Alta de Socios',
    description: 'Complete su solicitud de asociación al CAO',
    path: '/dashboard/alta-socios',
    active: true,
    color: 'bg-blue-500'
  },
  {
    icon: Users,
    title: 'Guía Profesionales',
    description: 'Publique sus datos en la guía de profesionales',
    path: '/dashboard/guia-profesionales',
    active: true,
    color: 'bg-emerald-500'
  },
  {
    icon: FileText,
    title: 'Trámites',
    description: 'Gestione sus trámites con el CAO',
    path: '/dashboard/tramites',
    coming: true,
    color: 'bg-purple-500'
  },
  {
    icon: Calendar,
    title: 'Eventos',
    description: 'Inscríbase a congresos y jornadas',
    path: '/dashboard/eventos',
    coming: true,
    color: 'bg-orange-500'
  }
];

const DashboardHome = () => {
  const { user, getAuthHeaders } = useAuth();
  const [socioData, setSocioData] = useState(null);
  const [guiaData, setGuiaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [socioRes, guiaRes] = await Promise.all([
          axios.get(`${API_URL}/api/socios/me`, { headers: getAuthHeaders() }).catch(() => null),
          axios.get(`${API_URL}/api/guia-profesionales/me`, { headers: getAuthHeaders() }).catch(() => null)
        ]);
        
        setSocioData(socioRes?.data);
        setGuiaData(guiaRes?.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [getAuthHeaders]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pendiente':
        return (
          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
            <Clock className="w-3 h-3 mr-1" />
            Pendiente
          </Badge>
        );
      case 'aprobado':
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Aprobado
          </Badge>
        );
      case 'rechazado':
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <AlertCircle className="w-3 h-3 mr-1" />
            Rechazado
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="cao-card p-6 bg-gradient-to-br from-[#2853B9] to-[#1E3876] text-white"
      >
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">
          Bienvenido/a, {user?.nombre}
        </h1>
        <p className="text-blue-100">
          Desde aquí puede gestionar su membresía y acceder a todos los servicios del CAO.
        </p>
      </motion.div>

      {/* Status Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Socio Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="cao-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold text-[#1E3876]">
                Estado de Asociación
              </CardTitle>
              <UserPlus className="w-5 h-5 text-[#3DA1E5]" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="animate-pulse h-20 bg-gray-100 rounded" />
              ) : socioData ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Estado:</span>
                    {getStatusBadge(socioData.estado)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Categoría:</span>
                    <span className="font-medium text-gray-900">
                      {socioData.datos?.categoria_socio?.replace(/_/g, ' ')?.charAt(0).toUpperCase() + 
                       socioData.datos?.categoria_socio?.replace(/_/g, ' ')?.slice(1) || 'N/A'}
                    </span>
                  </div>
                  <Link to="/dashboard/alta-socios">
                    <Button variant="outline" className="w-full mt-2" size="sm">
                      Ver detalles
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              ) : user?.role === 'socio' ? (
                <div className="text-center py-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                    <span className="text-green-600 font-semibold">Socio Verificado</span>
                  </div>
                  <p className="text-gray-500 mb-4">Su membresía está activa</p>
                  <Link to="/dashboard/mi-perfil">
                    <Button className="bg-[#2853B9] hover:bg-[#1E3876]">
                      Ver Mi Perfil
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-4">No ha completado su alta de socio</p>
                  <Link to="/dashboard/alta-socios">
                    <Button className="bg-[#2853B9] hover:bg-[#1E3876]">
                      Completar Alta
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Guia Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="cao-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold text-[#1E3876]">
                Guía de Profesionales
              </CardTitle>
              <Users className="w-5 h-5 text-[#3DA1E5]" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="animate-pulse h-20 bg-gray-100 rounded" />
              ) : guiaData ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Estado:</span>
                    {getStatusBadge(guiaData.estado)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Subespecialidad:</span>
                    <span className="font-medium text-gray-900 text-right text-sm">
                      {guiaData.datos?.subespecialidad_principal || 'N/A'}
                    </span>
                  </div>
                  <Link to="/dashboard/guia-profesionales">
                    <Button variant="outline" className="w-full mt-2" size="sm">
                      Ver detalles
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-4">No está en la guía de profesionales</p>
                  <Link to="/dashboard/guia-profesionales">
                    <Button className="bg-[#2853B9] hover:bg-[#1E3876]">
                      Solicitar Inscripción
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold text-[#1E3876] mb-4">Acciones Rápidas</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions
            .filter(action => {
              // Si es socio verificado, ocultar Alta de Socios
              if (user?.role === 'socio' && action.title === 'Alta de Socios') return false;
              return true;
            })
            .map((action, index) => (
            <motion.div
              key={action.path}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              <Link to={action.path}>
                <Card className="cao-card card-hover h-full">
                  <CardContent className="p-5">
                    <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center mb-4`}>
                      <action.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-[#1E3876] mb-1">{action.title}</h3>
                        <p className="text-sm text-gray-500">{action.description}</p>
                      </div>
                      {action.coming && (
                        <span className="coming-soon-badge shrink-0">Próx.</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Additional Services */}
      <div>
        <h2 className="text-xl font-semibold text-[#1E3876] mb-4">Más Servicios</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <Link to="/dashboard/e-commerce">
            <Card className="cao-card card-hover">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-pink-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#1E3876]">E-Commerce</h3>
                  <p className="text-sm text-gray-500">Librería CAO</p>
                </div>
                <span className="coming-soon-badge">Próx.</span>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/e-learning">
            <Card className="cao-card card-hover">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#1E3876]">E-Learning</h3>
                  <p className="text-sm text-gray-500">Cursos Online</p>
                </div>
                <span className="coming-soon-badge">Próx.</span>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/biblioteca">
            <Card className="cao-card card-hover">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#1E3876]">Biblioteca</h3>
                  <p className="text-sm text-gray-500">Recursos</p>
                </div>
                <span className="coming-soon-badge">Próx.</span>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
